import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Boxes, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button, EmptyState, Skeleton } from '../ui';
import { useMutation, useQuery } from '../gql/hooks';
import {
  CANONICAL_MODELS_QUERY,
  CREATE_CANONICAL_MODEL_MUTATION,
  DELETE_CANONICAL_MODEL_MUTATION,
  UPDATE_CANONICAL_MODEL_MUTATION,
  type CanonicalModelsQueryResult,
  type CreateCanonicalModelMutationResult,
  type CreateCanonicalModelMutationVariables,
  type DeleteCanonicalModelMutationResult,
  type DeleteCanonicalModelMutationVariables,
  type UpdateCanonicalModelMutationResult,
  type UpdateCanonicalModelMutationVariables,
} from '../gql/operations';
import { CanonicalModelKind, type CanonicalModel } from '../gql/types';
import { PageHeader } from '../features/shared/PageHeader';
import { TextField } from '../features/shared/FormField';
import { InlineBanner } from '../features/shared/InlineBanner';
import { Modal } from '../ui';

// Sortable-column metadata for the Models table — keyed by the column the operator clicks.
type SortColumn = 'name' | 'kind' | 'displayName' | 'providerCount';
type SortDir = 'asc' | 'desc';

function compareModels(
  a: CanonicalModel,
  b: CanonicalModel,
  column: SortColumn,
  dir: SortDir,
): number {
  let cmp = 0;
  switch (column) {
    case 'name':
      cmp = a.name.localeCompare(b.name);
      break;
    case 'kind':
      cmp = a.kind.localeCompare(b.kind);
      break;
    case 'displayName':
      cmp = (a.displayName ?? '').localeCompare(b.displayName ?? '');
      break;
    case 'providerCount':
      cmp = a.providerCount - b.providerCount;
      break;
  }
  // Stable tiebreaker so the same-value rows render in a deterministic order regardless of
  // which column the operator sorted by.
  if (cmp === 0) cmp = a.name.localeCompare(b.name);
  return dir === 'asc' ? cmp : -cmp;
}

// Canonical Models admin page (W6). One catalog of model identifiers (`auto` + the upstream
// + custom entries) that the routing engine, alias pickers, and Sandbox model picker all
// read from. `auto` is structural — disabled toggle is permitted (so an operator can take
// auto-routing offline) but delete + rename are blocked at the API.

const KIND_TONE: Record<
  CanonicalModelKind,
  { dark: string; light: string; label: string }
> = {
  [CanonicalModelKind.Auto]: {
    dark: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-300',
    light: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    label: 'auto',
  },
  [CanonicalModelKind.Upstream]: {
    dark: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
    light: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    label: 'upstream',
  },
  [CanonicalModelKind.Custom]: {
    dark: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
    light: 'border-amber-200 bg-amber-50 text-amber-700',
    label: 'custom',
  },
};

function KindChip({ kind }: { kind: CanonicalModelKind }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const tone = KIND_TONE[kind];
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${
        isDark ? tone.dark : tone.light
      }`}
    >
      {tone.label}
    </span>
  );
}

interface ModelFormState {
  name: string;
  displayName: string;
  description: string;
  enabled: boolean;
}

const EMPTY_FORM: ModelFormState = {
  name: '',
  displayName: '',
  description: '',
  enabled: true,
};

export function ModelsPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { data, loading, error, refetch } = useQuery<CanonicalModelsQueryResult>(
    CANONICAL_MODELS_QUERY,
  );

  const create = useMutation<
    CreateCanonicalModelMutationResult,
    CreateCanonicalModelMutationVariables
  >(CREATE_CANONICAL_MODEL_MUTATION);
  const update = useMutation<
    UpdateCanonicalModelMutationResult,
    UpdateCanonicalModelMutationVariables
  >(UPDATE_CANONICAL_MODEL_MUTATION);
  const remove = useMutation<
    DeleteCanonicalModelMutationResult,
    DeleteCanonicalModelMutationVariables
  >(DELETE_CANONICAL_MODEL_MUTATION);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CanonicalModel | null>(null);
  const [formState, setFormState] = useState<ModelFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Operator UX state for the Models table — search filter + sortable columns. Both layer
  // ON TOP of the enabled/disabled grouping so "Available models always first" stays the
  // top-level invariant.
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (col: SortColumn) => {
    if (sortColumn !== col) {
      setSortColumn(col);
      setSortDir('asc');
    } else {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    }
  };

  // Sort enabled rows to the top so operators see "what's actually selectable" without
  // scrolling past disabled noise. Within each group, apply the search filter and the
  // operator-selected column sort.
  const models = useMemo<CanonicalModel[]>(() => {
    const all = data?.canonicalModels ?? [];
    const q = search.trim().toLowerCase();
    const filtered =
      q === ''
        ? all
        : all.filter(
            (m) =>
              m.name.toLowerCase().includes(q) ||
              (m.displayName ?? '').toLowerCase().includes(q),
          );
    const sorted = [...filtered].sort((a, b) => compareModels(a, b, sortColumn, sortDir));
    const enabled = sorted.filter((m) => m.enabled);
    const disabled = sorted.filter((m) => !m.enabled);
    return [...enabled, ...disabled];
  }, [data, search, sortColumn, sortDir]);

  const enabledCount = useMemo(() => models.filter((m) => m.enabled).length, [models]);

  const openCreate = () => {
    setEditing(null);
    setFormState(EMPTY_FORM);
    setSubmitError(null);
    setFormOpen(true);
  };

  const openEdit = (model: CanonicalModel) => {
    setEditing(model);
    setFormState({
      name: model.name,
      displayName: model.displayName ?? '',
      description: model.description ?? '',
      enabled: model.enabled,
    });
    setSubmitError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const name = formState.name.trim();
    if (!name && !editing) {
      setSubmitError('Name is required.');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await update.mutate({
          input: {
            id: editing.id,
            name: name === editing.name ? undefined : name || undefined,
            displayName: formState.displayName.trim() || undefined,
            description: formState.description.trim() || undefined,
            enabled: formState.enabled,
          },
        });
      } else {
        await create.mutate({
          input: {
            name,
            displayName: formState.displayName.trim() || undefined,
            description: formState.description.trim() || undefined,
            enabled: formState.enabled,
          },
        });
      }
      closeForm();
      refetch();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (model: CanonicalModel) => {
    if (model.kind === CanonicalModelKind.Auto) return;
    if (
      !window.confirm(
        `Delete canonical model "${model.name}"? This is rejected if any provider still has an alias for it.`,
      )
    ) {
      return;
    }
    try {
      await remove.mutate({ id: model.id });
      refetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Delete failed.');
    }
  };

  const handleToggleEnabled = async (model: CanonicalModel) => {
    try {
      await update.mutate({
        input: { id: model.id, enabled: !model.enabled },
      });
      refetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Toggle failed.');
    }
  };

  return (
    <div>
      <PageHeader
        title="Models"
        description="Catalog of canonical model ids consumers can request. `auto` routes via each provider's default; `upstream` rows are pulled from provider refresh; `custom` entries are operator-defined."
        breadcrumbs={[{ label: 'Models' }]}
        actions={
          <Button
            variant="primary"
            onClick={openCreate}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            New canonical model
          </Button>
        }
      />

      {error && (
        <div
          role="alert"
          className={`mb-6 rounded-xl p-4 ${
            isDark
              ? 'border border-rose-500/20 bg-rose-500/10'
              : 'border border-rose-200 bg-rose-50'
          }`}
        >
          <p className={`text-sm ${isDark ? 'text-rose-300' : 'text-rose-700'}`}>
            Couldn’t load models: {error.message}
          </p>
        </div>
      )}

      {loading && (
        <div className="grid grid-cols-1 gap-3" data-testid="models-loading">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="block" height={64} />
          ))}
        </div>
      )}

      {!loading && (data?.canonicalModels?.length ?? 0) > 0 && (
        <div className="mb-3 flex items-center gap-2">
          <div className="relative max-w-md flex-1">
            <Search
              className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${
                isDark ? 'text-white/30' : 'text-slate-400'
              }`}
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or display name…"
              aria-label="Search canonical models"
              data-testid="models-search"
              className={`min-h-10 w-full rounded-xl border pl-9 pr-3 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 ${
                isDark
                  ? 'border-white/[0.08] bg-white/[0.04] text-white placeholder-white/30 focus:border-indigo-500'
                  : 'border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:border-indigo-500'
              }`}
            />
          </div>
          {search.trim() !== '' && (
            <span className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
              {models.length} match{models.length === 1 ? '' : 'es'}
            </span>
          )}
        </div>
      )}

      {!loading && !error && models.length === 0 && (
        <EmptyState
          icon={<Boxes className="h-16 w-16" aria-hidden="true" />}
          title={
            search.trim() !== '' ? 'No models match your search' : 'No canonical models yet'
          }
          description={
            search.trim() !== ''
              ? `Nothing matches "${search.trim()}". Clear the search to see the full catalog.`
              : 'Add a custom canonical model, or refresh a provider to import upstream entries.'
          }
          action={
            search.trim() !== ''
              ? { label: 'Clear search', onClick: () => setSearch('') }
              : { label: 'New canonical model', onClick: openCreate }
          }
        />
      )}

      {!loading && models.length > 0 && (
        <div
          className={`overflow-hidden rounded-2xl border ${
            isDark ? 'border-white/[0.06]' : 'border-slate-200'
          }`}
        >
          <table className="w-full text-sm">
            <thead>
              <tr
                className={
                  isDark
                    ? 'border-b border-white/[0.06] bg-white/[0.02] text-white/50'
                    : 'border-b border-slate-200 bg-slate-50 text-slate-500'
                }
              >
                <SortableHeader
                  column="name"
                  label="Name"
                  sortColumn={sortColumn}
                  sortDir={sortDir}
                  onToggle={toggleSort}
                  isDark={isDark}
                />
                <SortableHeader
                  column="kind"
                  label="Kind"
                  sortColumn={sortColumn}
                  sortDir={sortDir}
                  onToggle={toggleSort}
                  isDark={isDark}
                />
                <SortableHeader
                  column="displayName"
                  label="Display"
                  sortColumn={sortColumn}
                  sortDir={sortDir}
                  onToggle={toggleSort}
                  isDark={isDark}
                />
                <SortableHeader
                  column="providerCount"
                  label="Providers"
                  sortColumn={sortColumn}
                  sortDir={sortDir}
                  onToggle={toggleSort}
                  isDark={isDark}
                />
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest">
                  Enabled
                </th>
                <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-widest">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {models.map((model, idx) => {
                const isAuto = model.kind === CanonicalModelKind.Auto;
                // Sub-header rows: the first enabled row gets an "AVAILABLE" header above
                // it; the first disabled row (the one right after the last enabled one)
                // gets a "DISABLED" divider so operators see exactly where the section
                // changes. No-op when the catalog is fully one-sided.
                const showAvailableHeader = idx === 0 && model.enabled;
                const showDisabledHeader = idx === enabledCount && enabledCount > 0 && !model.enabled;
                return (
                  <FragmentWrapper key={model.id}>
                    {showAvailableHeader && (
                      <SectionHeaderRow
                        label="Available"
                        sub="Enabled — selectable by clients right now"
                        isDark={isDark}
                      />
                    )}
                    {showDisabledHeader && (
                      <SectionHeaderRow
                        label="Disabled"
                        sub="Hidden from clients — flip Enabled to expose them"
                        isDark={isDark}
                      />
                    )}
                    <tr
                      data-testid="models-row"
                      data-enabled={model.enabled ? 'true' : 'false'}
                      className={
                        isDark
                          ? 'border-b border-white/[0.04] last:border-0'
                          : 'border-b border-slate-100 last:border-0'
                      }
                    >
                    <td
                      className={`px-4 py-3 font-mono text-[13px] ${
                        isDark ? 'text-white' : 'text-slate-900'
                      }`}
                    >
                      {model.name}
                    </td>
                    <td className="px-4 py-3">
                      <KindChip kind={model.kind} />
                    </td>
                    <td
                      className={`px-4 py-3 ${
                        isDark ? 'text-white/60' : 'text-slate-600'
                      }`}
                    >
                      {model.displayName ?? <span className="opacity-50">—</span>}
                    </td>
                    <td
                      className={`px-4 py-3 ${
                        isDark ? 'text-white/60' : 'text-slate-600'
                      }`}
                    >
                      {model.providerCount}
                    </td>
                    <td className="px-4 py-3">
                      <label className="inline-flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={model.enabled}
                          onChange={() => void handleToggleEnabled(model)}
                          className="h-4 w-4"
                          aria-label={`${model.enabled ? 'Disable' : 'Enable'} ${model.name}`}
                        />
                        <span
                          className={`text-xs ${
                            isDark ? 'text-white/40' : 'text-slate-500'
                          }`}
                        >
                          {model.enabled ? 'On' : 'Off'}
                        </span>
                      </label>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(model)}
                          className={`text-xs font-medium ${
                            isDark
                              ? 'text-indigo-300 hover:text-indigo-200'
                              : 'text-indigo-600 hover:text-indigo-700'
                          }`}
                        >
                          Edit
                        </button>
                        {!isAuto && (
                          <button
                            type="button"
                            onClick={() => void handleDelete(model)}
                            disabled={model.providerCount > 0}
                            title={
                              model.providerCount > 0
                                ? 'Disable provider aliases first.'
                                : 'Delete this canonical model.'
                            }
                            className={`inline-flex items-center gap-1 text-xs font-medium ${
                              model.providerCount > 0
                                ? isDark
                                  ? 'cursor-not-allowed text-white/20'
                                  : 'cursor-not-allowed text-slate-300'
                                : isDark
                                  ? 'text-rose-300 hover:text-rose-200'
                                  : 'text-rose-600 hover:text-rose-700'
                            }`}
                            aria-label={`Delete ${model.name}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  </FragmentWrapper>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? `Edit ${editing.name}` : 'New canonical model'}
        subtitle={
          editing
            ? 'Adjust display fields or toggle availability. Renaming a referenced model is allowed but breaks routing for in-flight aliases.'
            : 'A new operator-curated entry. Use upstream refresh on a provider to import its catalog instead.'
        }
        icon={<Boxes className="h-7 w-7" aria-hidden="true" />}
        footer={
          <>
            <Button variant="secondary" fullWidth onClick={closeForm} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              fullWidth
              loading={submitting}
              onClick={() => void handleSubmit()}
              leftIcon={
                editing ? (
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Plus className="h-4 w-4" aria-hidden="true" />
                )
              }
            >
              {editing ? 'Save changes' : 'Create model'}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {submitError && (
            <InlineBanner tone="error" testId="canonical-model-error">
              {submitError}
            </InlineBanner>
          )}

          <TextField
            label="Name"
            required={!editing}
            value={formState.name}
            onChange={(e) => setFormState({ ...formState, name: e.target.value })}
            placeholder="e.g. claude-3-opus"
            disabled={editing?.kind === CanonicalModelKind.Auto}
            hint={
              editing?.kind === CanonicalModelKind.Auto
                ? 'The `auto` row is reserved and cannot be renamed.'
                : 'Wire identifier. Lowercase, alphanumeric with `.`, `_`, `:`, `-`, `/` allowed.'
            }
          />

          <TextField
            label="Display name"
            value={formState.displayName}
            onChange={(e) =>
              setFormState({ ...formState, displayName: e.target.value })
            }
            placeholder="Optional — shown in the SPA pickers"
          />

          <TextField
            label="Description"
            value={formState.description}
            onChange={(e) =>
              setFormState({ ...formState, description: e.target.value })
            }
            placeholder="Optional notes — capabilities, residency, recommended uses"
          />

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={formState.enabled}
              onChange={(e) =>
                setFormState({ ...formState, enabled: e.target.checked })
              }
              className="h-4 w-4"
            />
            <span className={isDark ? 'text-white/70' : 'text-slate-700'}>
              Enabled (clients can select this model)
            </span>
          </label>
        </div>
      </Modal>
    </div>
  );
}

// Tiny passthrough so the `tbody.map` callback can return MULTIPLE elements (the optional
// section-header row + the model row) without React complaining about an extra wrapper.
// We use a Fragment with a key, but keys can't be applied directly to `<>` shorthand;
// `<Fragment key=…>` is the canonical workaround.
import { Fragment } from 'react';

function FragmentWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  return <Fragment>{children}</Fragment>;
}

function SortableHeader({
  column,
  label,
  sortColumn,
  sortDir,
  onToggle,
  isDark,
}: {
  column: SortColumn;
  label: string;
  sortColumn: SortColumn;
  sortDir: SortDir;
  onToggle: (col: SortColumn) => void;
  isDark: boolean;
}) {
  const active = sortColumn === column;
  return (
    <th
      className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-widest"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onToggle(column)}
        data-testid={`models-sort-${column}`}
        className={`inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 ${
          isDark
            ? active
              ? 'text-white/80'
              : 'text-white/50 hover:text-white/70'
            : active
              ? 'text-slate-700'
              : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {label}
        {active &&
          (sortDir === 'asc' ? (
            <ArrowUp className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ArrowDown className="h-3 w-3" aria-hidden="true" />
          ))}
      </button>
    </th>
  );
}

// Rendered between the "Available" and "Disabled" model groups so operators see exactly
// where the section changes. Spans the whole table width via colSpan.
function SectionHeaderRow({
  label,
  sub,
  isDark,
}: {
  label: string;
  sub: string;
  isDark: boolean;
}) {
  return (
    <tr
      data-testid="models-section-header"
      data-section={label.toLowerCase()}
      className={isDark ? 'bg-white/[0.02]' : 'bg-slate-50/80'}
    >
      <td
        colSpan={6}
        className={`px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest ${
          isDark ? 'text-white/50' : 'text-slate-500'
        }`}
      >
        <span className={isDark ? 'text-white/80' : 'text-slate-700'}>{label}</span>
        <span className={`ml-2 normal-case tracking-normal ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
          · {sub}
        </span>
      </td>
    </tr>
  );
}
